// Reminders. A task's reminder is an instant (tasks.remind_at). Its owner sets
// and reads it as a wall time (YYYY-MM-DDTHH:MM) in their profile's time zone,
// the zone the database reads it in (set_task_reminder). These helpers format
// and compare in a zone they are given; they never assume one.

import { formatClockTime } from './clock.ts';
import { calendarDaysBetween, formatCalendarDate, isCalendarDate } from './today.ts';
import type { Task } from './types';

const WALL_TIME = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/;

/**
 * "2026-09-12T15:35": the wall time of `at` in `timeZone`, to the minute, on
 * a 24-hour clock. Throws a RangeError for a zone this runtime does not know.
 */
export function wallTimeIn(timeZone: string, at: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const part = (type: string) => parts.find(p => p.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

/** A real wall time: YYYY-MM-DDTHH:MM with a real date, an hour 00-23 and a minute 00-59. */
export function isWallTime(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = WALL_TIME.exec(value);
  return match !== null && isCalendarDate(match[1]) && Number(match[2]) <= 23 && Number(match[3]) <= 59;
}

/** "7:00 PM" or "19:00" for a wall time's clock, in the device's style (clock.ts). */
const clockText = (wall: string): string => formatClockTime(wall.slice(11, 16));

/** "Today, 7:00 PM", "Tomorrow, 9:00 AM" or "Tue, Sep 15, 3:00 PM": a wall time as it reads on `now`'s day in `timeZone`. */
export function describeWallTime(wall: string, timeZone: string, now: Date): string {
  const day = wall.slice(0, 10);
  const days = calendarDaysBetween(wallTimeIn(timeZone, now).slice(0, 10), day);
  const dayText = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : days === -1 ? 'Yesterday' : formatCalendarDate(day);
  return `${dayText}, ${clockText(wall)}`;
}

/**
 * "today at 7:00 PM", "yesterday at 3:55 PM" or "Tue, Sep 1 at 3:55 PM": a
 * wall time as it reads inside a sentence, on `now`'s day in `timeZone`. With
 * no zone there is no "today" to measure from, so it reads by its date.
 */
export function wallTimePhrase(wall: string, timeZone: string | null, now: Date): string {
  const day = wall.slice(0, 10);
  const days = timeZone === null ? null : calendarDaysBetween(wallTimeIn(timeZone, now).slice(0, 10), day);
  const dayText = days === 0 ? 'today' : days === 1 ? 'tomorrow' : days === -1 ? 'yesterday' : formatCalendarDate(day);
  return `${dayText} at ${clockText(wall)}`;
}

export interface ReminderView {
  /** The reminder's wall time in the owner's zone. */
  wall: string;
  text: string;
  /** Its time has come. */
  passed: boolean;
}

/** A reminder instant as its owner reads it in their zone. */
export function describeReminder(remindAt: string, timeZone: string, now: Date): ReminderView {
  const at = new Date(remindAt);
  const wall = wallTimeIn(timeZone, at);
  return { wall, text: describeWallTime(wall, timeZone, now), passed: at.getTime() <= now.getTime() };
}

/** The reminder a task row shows: one set on an open task and still to come; otherwise null. */
export function upcomingReminder(task: Pick<Task, 'remind_at' | 'status'>, now: Date): string | null {
  if (!task.remind_at || task.status === 'done') return null;
  return new Date(task.remind_at).getTime() > now.getTime() ? task.remind_at : null;
}

/**
 * How Settings describes the profile's zone: the zone, with the device's when
 * it differs; or, with none set, that there is no digest and no reminder
 * until there is, and why this device did not set it, when that is known.
 */
export function describeTimeZone(zone: string | null, device: string | null, problem: string | null): string {
  if (!zone) return `Not set, so there is no digest and no reminders${problem ? `. ${problem}` : ''}`;
  return device && device !== zone ? `${zone} (this device is in ${device})` : zone;
}

/** Where a reminder picker starts: the reminder's own wall time while it is still to come, else the next whole hour. */
export function reminderPickerStart(remindAt: string | null, timeZone: string, now: Date): string {
  if (remindAt && new Date(remindAt).getTime() > now.getTime()) return wallTimeIn(timeZone, new Date(remindAt));
  const wall = wallTimeIn(timeZone, now);
  const next = new Date(Date.UTC(
    Number(wall.slice(0, 4)), Number(wall.slice(5, 7)) - 1, Number(wall.slice(8, 10)), Number(wall.slice(11, 13)) + 1,
  ));
  return next.toISOString().slice(0, 16);
}
